import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { getTranslatedText } from '@nile/components';
import { RMMClient } from '@nile/graphql';
import {
  RESOLVE_ALERT,
  DELETE_ALERTS
} from '@nile/rmm/src/modules/alert-management/Graphql/alert';

// Types
interface AlertAsset {
  assetId: string;
  name: string;
  assetClass?: {
    classId: string;
    name?: string;
  };
  client?: {
    name: string;
    accountId: string;
  };
  site?: {
    name: string;
    id: string;
  };
}

interface AlertPolicy {
  name: string;
  policyType?: string;
}

export interface SelectedAlertForOccurrences {
  id: string;
  message: string;
  severity: string;
  status: string;
  description: string;
  createdTime: string;
  resolvedTime: string;
  occurrenceCount: number;
  asset: AlertAsset;
  policy: AlertPolicy;
}

interface AlertData {
  id?: string;
  message?: string;
  severity?: string;
  status?: string;
  description?: string;
  createdTime?: string;
  resolvedTime?: string | null;
  occurrenceCount?: number;
  asset?: AlertAsset;
  policy?: AlertPolicy;
}

interface UseAlertOccurrencesModalOptions {
  intl?: any;
  onResolveCompleted?: () => void;
  onDeleteCompleted?: () => void;
  onResolveError?: (error: any) => void;
  onDeleteError?: (error: any) => void;
  showActionBtns?: boolean;
}

interface UseAlertOccurrencesModalReturn {
  occurrencesModalOpen: boolean;
  selectedAlertForOccurrences: SelectedAlertForOccurrences | null;
  handleViewOccurrences: (alertData: AlertData) => void;
  closeModal: () => void;
  modalProps: {
    isOpen: boolean;
    onClose: () => void;
    alertDetails: SelectedAlertForOccurrences | null;
    showActionBtns: boolean;
    onResolve?: (alertId: string) => void;
    onDelete?: (alertId: string) => void;
    resolvingAlert?: boolean;
    deletingAlert?: boolean;
  };
}

/**
 * Custom hook to manage Alert Occurrences Modal state and handlers
 *
 * @param options - Configuration options for the hook
 * @param options.intl - Internationalization object for translations
 * @param options.onResolveCompleted - Optional callback when resolve mutation completes successfully
 * @param options.onDeleteCompleted - Optional callback when delete mutation completes successfully
 * @param options.onResolveError - Optional callback when resolve mutation fails
 * @param options.onDeleteError - Optional callback when delete mutation fails
 * @param options.showActionBtns - Whether to show action buttons (Resolve, Delete)
 * @returns Object containing modal state, handlers, and props for AlertOccurrencesModal
 */
export const useAlertOccurrencesModal = (
  options: UseAlertOccurrencesModalOptions = {}
): UseAlertOccurrencesModalReturn => {
  const {
    intl,
    onResolveCompleted,
    onDeleteCompleted,
    onResolveError,
    onDeleteError,
    showActionBtns = true
  } = options;

  const [occurrencesModalOpen, setOccurrencesModalOpen] = useState(false);
  const [
    selectedAlertForOccurrences,
    setSelectedAlertForOccurrences
  ] = useState<SelectedAlertForOccurrences | null>(null);

  const closeModal = useCallback(() => {
    setOccurrencesModalOpen(false);
    setSelectedAlertForOccurrences(null);
  }, []);

  // Resolve mutation
  const [resolveRespectiveAlert, { loading: resolvingAlert }] = useMutation(
    RESOLVE_ALERT,
    {
      client: RMMClient,
      onCompleted: () => {
        if (onResolveCompleted) {
          onResolveCompleted();
        }
        closeModal();
      },
      onError: onResolveError
    }
  );

  // Delete mutation
  const [deleteRespectiveAlert, { loading: deletingAlert }] = useMutation(
    DELETE_ALERTS,
    {
      client: RMMClient,
      onCompleted: () => {
        if (onDeleteCompleted) {
          onDeleteCompleted();
        }
        closeModal();
      },
      onError: onDeleteError
    }
  );

  const handleViewOccurrences = useCallback(
    (alertData: AlertData) => {
      setSelectedAlertForOccurrences({
        id: alertData?.id || '',
        message: alertData?.message || '',
        severity: alertData?.severity || '',
        status: alertData?.status || '',
        description: alertData?.description || '',
        createdTime: alertData?.createdTime || '',
        resolvedTime: alertData?.resolvedTime
          ? alertData.resolvedTime
          : (intl ? getTranslatedText(intl, 'app.noData.message') : '') || '',
        occurrenceCount: alertData?.occurrenceCount || 0,
        asset: alertData?.asset || {
          assetId: '',
          name: ''
        },
        policy: alertData?.policy || {
          name: ''
        }
      });
      setOccurrencesModalOpen(true);
    },
    [intl]
  );

  const handleResolve = useCallback(
    (alertId: string) => {
      resolveRespectiveAlert({
        variables: { alertId }
      });
    },
    [resolveRespectiveAlert]
  );

  const handleDelete = useCallback(
    (alertId: string) => {
      deleteRespectiveAlert({
        variables: {
          alertIds: [alertId]
        }
      });
    },
    [deleteRespectiveAlert]
  );

  return {
    occurrencesModalOpen,
    selectedAlertForOccurrences,
    handleViewOccurrences,
    closeModal,
    modalProps: {
      isOpen: occurrencesModalOpen,
      onClose: closeModal,
      alertDetails: selectedAlertForOccurrences,
      showActionBtns,
      onResolve: handleResolve,
      onDelete: handleDelete,
      resolvingAlert,
      deletingAlert
    }
  };
};
